<template>
    <div>
        <div class="table-controls | u-text-right">
            <div class="page-block__header">
                <h1 class="tabulator__title">{{ trans('page.services.title') }}</h1>
                <div>
                    <button
                        v-if="showAddDnsButton"
                        class="btn btn--ghost btn--small | u-margin-right-narrow"
                        dusk="add-dns-button"
                        @click="showModal"
                    >
                        {{ trans('action.add-dns') }}
                    </button>
                    <button class="btn btn--ghost btn--small" @click="downloadCsv">
                        {{ trans('action.download.csv') }}
                    </button>
                </div>
            </div>
        </div>
        <div class="tabulator">
            <div ref="pagination" class="tabulator-footer"></div>
        </div>
        <div ref="table"></div>

        <add-dns-modal v-if="showDnsModal" :form="form" :isLoading="isLoading" @submit="addDns" @close="closeModal" />
    </div>
</template>

<script>
import Tabulator from 'tabulator-tables';
import AddDnsModal from './AddDnsModal.vue';
import { formMixin, Way2Form } from 'relapp-way2forms';
import { DomainSanitizationFactory } from 'relapp-domain-sanitization';
import { apiMixin } from 'relapp-api';
import ServicesApi from '../services/api/service.js';
import '../styles/_tabulator.scss';

const domainSanitizationFactory = new DomainSanitizationFactory();

export default {
    mixins: [formMixin, apiMixin],
    components: {
        AddDnsModal
    },
    props: {
        showCustomerColumn: {
            type: Boolean,
            default: false
        },
        showAddDnsButton: {
            type: Boolean,
            default: false
        },
        tableData: {
            type: Array,
            required: true
        },
        isLoading: {
            type: Boolean,
            required: true
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        let extraColumns = [];

        if (this.showCustomerColumn) {
            extraColumns.push({
                title: trans('services.customer'),
                field: 'customer',
                headerFilter: 'input',
                headerFilterParams: { values: true },
                headerFilterPlaceholder: 'filter'
            });
        }

        return {
            tabulator: null,
            columns: [
                ...extraColumns,
                {
                    title: trans('services.hostname'),
                    field: 'hostname',
                    headerFilter: 'input',
                    headerFilterParams: { values: true },
                    headerFilterPlaceholder: 'filter',
                    width: 350
                },
                {
                    field: 'dns',
                    title: trans('services.dns'),
                    formatter: cell => this.booleanFormatter(cell.getValue()),
                    headerFilter: 'tickCross',
                    headerFilterParams: { tristate: true },
                    headerFilterPlaceholder: 'filter'
                },
                {
                    title: trans('services.domain'),
                    field: 'domain',
                    formatter: cell => this.booleanFormatter(cell.getValue()),
                    headerFilter: 'tickCross',
                    headerFilterParams: { tristate: true },
                    headerFilterPlaceholder: 'filter'
                },
                {
                    title: trans('services.hosting'),
                    field: 'hosting',
                    formatter: cell => this.valueOrFalseFormatter(cell.getValue()),
                    headerFilter: 'select',
                    headerFilterParams: this.hostingFilter,
                    headerFilterPlaceholder: 'filter'
                },
                {
                    title: trans('services.ssl'),
                    field: 'ssl',
                    formatter: cell => this.valueOrFalseFormatter(cell.getValue()),
                    headerFilter: 'select',
                    headerFilterParams: this.sslFilter,
                    headerFilterPlaceholder: 'filter'
                }
            ],
            form: new Way2Form({
                domain: ''
            }),
            showDnsModal: false
        };
    },
    watch: {
        tableData: {
            /**
             * Watcher for the tableData
             *
             * @param {array} newData
             */
            handler(newData) {
                this.tabulator.replaceData(newData);
            },
            deep: true
        }
    },
    /**
     * Called after the instance has been mounted.
     */
    mounted() {
        this.tabulator = new Tabulator(this.$refs.table, {
            ...tabulatorConfig.default,
            paginationElement: this.$refs.pagination,
            rowFormatter: row => {
                const data = row.getData();

                row.getElement().setAttribute('data-domain', data.hostname);
                if (data.domain) {
                    row.getElement().setAttribute('data-has-domain', true);
                } else {
                    row.getElement().setAttribute('data-has-domain', false);
                }
                if (data.hosting) {
                    row.getElement().setAttribute('data-has-hosting', true);
                } else {
                    row.getElement().setAttribute('data-has-hosting', false);
                }
                if (data.ssl) {
                    row.getElement().setAttribute('data-has-ssl', true);
                } else {
                    row.getElement().setAttribute('data-has-ssl', false);
                }
            },
            columns: this.columns,
            data: this.tableData,
            selectable: true,
            index: 'hostname',
            langs: {
                nl: {
                    pagination: {
                        page_size: trans('pagination.results'),
                        first: trans('pagination.first'),
                        first_title: trans('pagination.first_title'),
                        last: trans('pagination.last'),
                        last_title: trans('pagination.last_title'),
                        prev: trans('pagination.previous'),
                        prev_title: trans('pagination.previous_title'),
                        next: trans('pagination.next'),
                        next_title: trans('pagination.next_title')
                    }
                }
            },
            rowClick: (e, row) => this.$router.push({ name: 'dnsOverview', params: { domain: row.getIndex() } })
        });

        this.tabulator.setLocale('nl-nl');
    },
    methods: {
        /**
         * Download the contents of the table in CSV format.
         */
        downloadCsv() {
            this.tabulator.download('csv', 'data.csv');
        },
        /**
         * Tabulator formatter for booleans.
         *
         * @param  {boolean} value
         *
         * @return {any}
         */
        booleanFormatter(value) {
            if (value === true) {
                return trans('boolean.true');
            }

            if (value === false) {
                return trans('boolean.false');
            }

            return value;
        },
        /**
         * Tabulator formatter for optional values.
         *
         * @param  {any} value
         *
         * @return {any}
         */
        valueOrFalseFormatter(value) {
            if (value === false) {
                return trans('boolean.false');
            }

            return value;
        },
        /**
         * Get the filter parameters for the hosting column.
         *
         * @return {object}
         */
        hostingFilter() {
            let values = {};

            this.tableData.forEach(row => {
                values[row.hosting] = this.valueOrFalseFormatter(row.hosting);
            });

            return { values };
        },
        /**
         * Get the filter parameters for the ssl column.
         *
         * @return {object}
         */
        sslFilter() {
            let values = {};

            this.tableData.forEach(row => {
                values[row.ssl] = this.valueOrFalseFormatter(row.ssl);
            });

            return { values };
        },
        /**
         * Show the modal.
         */
        showModal() {
            this.showDnsModal = true;
        },

        /**
         * Close the modal.
         */
        closeModal() {
            this.showDnsModal = false;
        },
        /**
         * Call the endpoint to add a new dns.
         */
        addDns() {
            const domainSanitizationResult = domainSanitizationFactory.sanitize(this.form.domain);

            this.form.domain = domainSanitizationResult.sanitizedInput;

            const dnsService = {
                domain: this.form.domain,
                name: 'DNS',
                period: 12,
                price: 0,
                gross_price: 0,
                status: 'registration'
            };

            const postData = {
                total_price: 0,
                subscriptions: {
                    dns: [dnsService]
                }
            };

            this.$emit('startLoading');
            this.form.errors.clear();
            ServicesApi.add(postData)
                .then(response => {
                    this.form.onSuccess(response);
                    this.closeModal();
                    this.$emit('dnsAdded');
                })
                .catch(error => {
                    this.showValidationErrors(error, this.form);
                })
                .finally(() => this.$emit('finishLoading'));
        }
    }
};
</script>
